/**
 * ManagerView (Screen 4) — manager dashboard (per-rep score trends) + the
 * CP3 COACHING INBOX.
 *
 * Manager/admin only (the route is gated in main.tsx; the server also enforces
 * the role from the JWT). Renders:
 *   - the coaching inbox: every open coaching item from GET /api/manager/queue,
 *     surfaced through the six-state lifecycle (sql/021):
 *       needs_review → assigned → acknowledged → follow_up → improved → closed
 *     with per-state counts, a state filter, a status chip per row, and a
 *     legal-transition "Move to…" control that PATCHes /api/calls/:id/flag.
 *     Closed items leave the inbox (the queue returns non-closed only).
 *   - the GET /api/manager/dashboard rollup: overall summary, one Card per rep
 *     (clickable → the CP3 agent profile at /manager/agents/:id), and a
 *     recent-scored-calls table where each row links to /calls/:id.
 *
 * AI scores feed this dashboard; calls only appear once they reach the "scored"
 * pipeline status, so the empty state nudges the manager to flag calls for AI
 * review. All score math is server-side (avg over scored calls only).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { FlagLifecycleStatus, ManagerQueueItem } from "@/types";
import { FLAG_STATUS_LABELS, OPEN_FLAG_STATUSES } from "@/lib/coachingLifecycle";
import { FlagStatusBadge, FlagTransitionSelect } from "@/components/FlagLifecycle";
import { PageHeader, Card, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { IconInfo } from "@/components/icons";

type BadgeTone = "muted" | "primary" | "success" | "warning" | "danger";

/**
 * Source-tagging legend (Phase 5, D-TRENDS). Describes ACTUAL behavior: the
 * dashboard does NOT yet split trends by transcript source — every scored call
 * currently feeds the trend equally. Transcript provenance (live vs. recording)
 * IS tracked per call, and source-aware trend weighting is planned once live
 * sourcing is enabled. Do NOT claim a server-side filter here that the dashboard
 * endpoint does not implement (while live sourcing is dark, all calls are
 * recording-sourced, so such a filter would empty the trends entirely).
 */
function SourceTaggingLegend() {
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-dashed bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground">
      <IconInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        Each scored call records its transcript source — live (rep/customer roles
        known) or recording (roles inferred by diarization). Trends include all
        scored calls today; source-aware weighting of rep-behavior trends is coming
        once live transcript sourcing is enabled.
      </span>
    </p>
  );
}

/** Score band → Badge tone. >=70 green, 50–69 amber, <50 red. */
function scoreTone(total: number): BadgeTone {
  if (total >= 70) return "success";
  if (total >= 50) return "warning";
  return "danger";
}

/** Coaching flag type → Badge tone + label. Includes the system 'auto_review'
 *  flag (D9) the scorer raises (low score / critical miss / needs review). */
function flagTone(type: string): BadgeTone {
  if (type === "exemplary") return "success";
  if (type === "auto_review") return "danger";
  return "warning";
}
function flagLabel(type: string): string {
  if (type === "exemplary") return "Exemplary";
  if (type === "auto_review") return "Auto review";
  return "Needs work";
}

/** Same band as a bare text color, for the big per-rep number. */
function scoreTextColor(total: number): string {
  if (total >= 70) return "text-emerald-600 dark:text-emerald-400";
  if (total >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function fmtShortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Dependency-free trend: a row of small vertical bars, one per recent score,
 * oldest→newest, height ∝ total/100. Pure divs + CSS, no charting lib.
 */
function Trend({ recent }: { recent: number[] }) {
  if (recent.length === 0) {
    return <span className="text-xs text-muted-foreground">No trend yet</span>;
  }
  return (
    <div
      className="flex h-10 items-end gap-1"
      role="img"
      aria-label={`Recent scores, oldest to newest: ${recent.join(", ")}`}
    >
      {recent.map((total, i) => {
        const clamped = Math.max(0, Math.min(100, total));
        return (
          <div
            key={i}
            title={`${total}/100`}
            className="w-2 rounded-sm bg-current opacity-80"
            // min 8% so a zero/low score still shows a sliver.
            style={{ height: `${Math.max(8, clamped)}%` }}
          />
        );
      })}
    </div>
  );
}

/** One state-filter chip (label + count) for the coaching inbox. */
function StateChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
        active
          ? "border-primary bg-primary/10 text-primary"
          : "bg-background text-muted-foreground hover:bg-accent/40"
      }`}
    >
      {label}
      <span className={`tabular-nums ${active ? "font-semibold" : ""}`}>{count}</span>
    </button>
  );
}

/**
 * Coaching inbox — every open coaching item (GET /api/manager/queue), surfaced
 * through the lifecycle: per-state counts, a state filter, a status chip per
 * row, and a legal-transition "Move to…" select (PATCH /api/calls/:id/flag).
 * Closing an item removes it from the inbox (the queue is non-closed only).
 */
function CoachingInbox() {
  const queryClient = useQueryClient();
  const [stateFilter, setStateFilter] = useState<"" | FlagLifecycleStatus>("");
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const {
    data: queue,
    isLoading: queueLoading,
    isError: queueIsError,
    error: queueError,
    refetch: refetchQueue,
  } = useQuery({
    queryKey: queryKeys.manager.queue,
    queryFn: ({ signal }) => api.getManagerQueue(signal),
  });

  const transition = useMutation({
    // flagId pins the exact row server-side — a call can carry both a manual
    // and an auto_review flag, and this row's id is authoritative.
    mutationFn: (vars: { flagId: string; callId: string; status: FlagLifecycleStatus }) =>
      api.transitionFlag(vars.callId, vars.status, vars.flagId),
    onMutate: () => setTransitionError(null),
    onError: (err) => {
      setTransitionError(
        err instanceof ApiRequestError && err.body?.message
          ? err.body.message
          : "Couldn't update the coaching item. Please try again.",
      );
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.manager.queue });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.coaching(vars.callId),
      });
    },
  });

  const items: ManagerQueueItem[] = queue ?? [];

  // Per-state counts over the whole inbox (not the filtered view).
  const counts = useMemo(() => {
    const m = new Map<FlagLifecycleStatus, number>();
    for (const it of items) m.set(it.status, (m.get(it.status) ?? 0) + 1);
    return m;
  }, [items]);

  const visible = stateFilter
    ? items.filter((it) => it.status === stateFilter)
    : items;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Coaching inbox</h2>
        {items.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <StateChip
              active={stateFilter === ""}
              label="All open"
              count={items.length}
              onClick={() => setStateFilter("")}
            />
            {OPEN_FLAG_STATUSES.map((s) => (
              <StateChip
                key={s}
                active={stateFilter === s}
                label={FLAG_STATUS_LABELS[s]}
                count={counts.get(s) ?? 0}
                onClick={() => setStateFilter(stateFilter === s ? "" : s)}
              />
            ))}
          </div>
        )}
      </div>

      {queueLoading && <LoadingState label="Loading coaching inbox…" />}
      {queueIsError && (
        <ErrorState error={queueError} onRetry={() => void refetchQueue()} />
      )}
      {!queueLoading && !queueIsError && items.length === 0 && (
        <EmptyState
          title="No open coaching items"
          description="Flag a call as exemplary or needs-work to add it here."
        />
      )}
      {!queueLoading && !queueIsError && items.length > 0 && visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No items in “{stateFilter ? FLAG_STATUS_LABELS[stateFilter] : "All open"}”.
        </p>
      )}
      {!queueLoading && !queueIsError && visible.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Rep</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Score</th>
                  <th className="px-4 py-2 font-medium">Note</th>
                  <th className="px-4 py-2 font-medium">Assigned</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Move</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b last:border-0 hover:bg-accent/40"
                  >
                    <td className="px-4 py-2">
                      {item.rep_id ? (
                        <Link
                          className="font-medium hover:underline"
                          to={`/manager/agents/${item.rep_id}`}
                          title="Open agent profile"
                        >
                          {item.rep_name ?? "Unassigned"}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">
                          {item.rep_name ?? "Unassigned"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={flagTone(item.type)}>
                        {flagLabel(item.type)}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <FlagStatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-2">
                      {item.total === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Badge tone={scoreTone(item.total)}>
                          {item.total}/100
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 max-w-xs">
                      {item.note ? (
                        <span className="line-clamp-2 text-muted-foreground">
                          {item.note}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {item.assigned_to_name ? (
                        <span>
                          {item.assigned_to_name}
                          {item.due_date
                            ? ` · due ${fmtShortDate(item.due_date)}`
                            : ""}
                          {item.acknowledged_at ? " · ✓ seen" : ""}
                        </span>
                      ) : (
                        "Unassigned"
                      )}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <Link
                        className="hover:underline"
                        to={`/calls/${item.call_id}`}
                        title="Open the call"
                      >
                        {item.started_at
                          ? new Date(item.started_at).toLocaleString()
                          : "View call"}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <FlagTransitionSelect
                        status={item.status}
                        pending={
                          transition.isPending &&
                          transition.variables?.flagId === item.id
                        }
                        onTransition={(to) =>
                          transition.mutate({
                            flagId: item.id,
                            callId: item.call_id,
                            status: to,
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {transitionError && (
        <p className="text-sm text-destructive">{transitionError}</p>
      )}
    </section>
  );
}

export function ManagerView() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.manager.dashboard,
    queryFn: ({ signal }) => api.getManagerDashboard(signal),
  });

  const hasData = !!data && data.overall.scored_count > 0;

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="Manager dashboard"
        subtitle="Coaching inbox and per-rep score trends across scored calls."
      />

      <div className="flex flex-1 flex-col gap-6 p-5">
        {/* Coaching inbox — open coaching items through the CP3 lifecycle.
            Independent of the scored-call rollup below, so it renders on its
            own. */}
        <CoachingInbox />

        {isLoading && <LoadingState label="Loading dashboard…" />}
        {isError && <ErrorState error={error} onRetry={() => void refetch()} />}

        {!isLoading && !isError && !hasData && (
          <EmptyState
            title="No scored calls yet"
            description="Flag calls for AI review to populate this. Once a call is scored, its rep's trend and the team average appear here."
          />
        )}

        {!isLoading && !isError && hasData && data && (
          <>
            {/* Overall summary row */}
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Scored calls
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums">
                  {data.overall.scored_count}
                </p>
              </Card>
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Average score
                </p>
                <p
                  className={`mt-1 text-3xl font-semibold tabular-nums ${scoreTextColor(
                    data.overall.avg_total,
                  )}`}
                >
                  {data.overall.avg_total}
                  <span className="ml-1 text-base font-normal text-muted-foreground">
                    /100
                  </span>
                </p>
              </Card>
            </section>

            {/* Per-rep cards */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">By rep</h2>
              <SourceTaggingLegend />
              {data.reps.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No reps have scored calls yet.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {data.reps.map((rep) => {
                    const key =
                      rep.user_id ?? `${rep.name}-${rep.extension_number ?? ""}`;
                    const card = (
                      <Card
                        className={
                          rep.user_id
                            ? "h-full transition hover:border-primary/60 hover:shadow-md"
                            : undefined
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-medium">{rep.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {rep.extension_number
                                ? `Ext ${rep.extension_number}`
                                : "No extension"}
                            </p>
                          </div>
                          <Badge tone={scoreTone(rep.avg_total)}>
                            {rep.avg_total}/100
                          </Badge>
                        </div>

                        <div className="mt-3 flex items-end justify-between gap-3">
                          <div className={scoreTextColor(rep.avg_total)}>
                            <Trend recent={rep.recent} />
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            <p>
                              <span className="font-medium tabular-nums text-foreground">
                                {rep.scored_count}
                              </span>{" "}
                              scored
                            </p>
                            <p>Last {fmtShortDate(rep.last_scored_at)}</p>
                          </div>
                        </div>
                      </Card>
                    );
                    // Attributed reps drill into the CP3 agent profile; the
                    // "Unassigned" bucket has no profile to open.
                    return rep.user_id ? (
                      <Link
                        key={key}
                        to={`/manager/agents/${rep.user_id}`}
                        className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title={`Open ${rep.name}'s agent profile`}
                      >
                        {card}
                      </Link>
                    ) : (
                      <div key={key}>{card}</div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Recent scored calls */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Recent scored calls</h2>
              {data.recent_calls.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No recently scored calls.
                </p>
              ) : (
                <Card className="overflow-hidden p-0">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 font-medium">Rep</th>
                        <th className="px-4 py-2 font-medium">Ext</th>
                        <th className="px-4 py-2 font-medium">Score</th>
                        <th className="px-4 py-2 font-medium">Direction</th>
                        <th className="px-4 py-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_calls.map((c) => (
                        <tr
                          key={c.call_id}
                          className="border-b last:border-0 hover:bg-accent/40"
                        >
                          <td className="px-4 py-2">
                            <Link
                              className="hover:underline"
                              to={`/calls/${c.call_id}`}
                            >
                              {c.rep_name}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {c.extension_number ?? "—"}
                          </td>
                          <td className="px-4 py-2">
                            <Badge tone={scoreTone(c.total)}>{c.total}/100</Badge>
                          </td>
                          <td className="px-4 py-2">{c.direction}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {c.started_at
                              ? new Date(c.started_at).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
